import type { Milli, Paisa } from './money.js';

/** Calendar date as YYYY-MM-DD, the business date the user chose. */
export type ISODate = string;

export const UNITS = ['pcs', 'set', 'box', 'litre', 'kg', 'pair', 'metre'] as const;
export type Unit = (typeof UNITS)[number];

export const PARTY_TYPES = ['customer', 'vendor'] as const;
export type PartyType = (typeof PARTY_TYPES)[number];

/** Where money sits: the cash box, a bank account, or a mobile wallet (bKash, Nagad, Rocket…). */
export const ACCOUNT_KINDS = ['cash', 'bank', 'mobile'] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];
/** Display order everywhere accounts are listed: cash first, then banks, then mobile wallets. */
export const ACCOUNT_KIND_ORDER: Readonly<Record<AccountKind, number>> = {
  cash: 0,
  bank: 1,
  mobile: 2,
};

export const RETURN_REASONS = ['defective', 'wrong_item', 'not_needed', 'damaged'] as const;
export type ReturnReason = (typeof RETURN_REASONS)[number];

export const ADJUST_REASONS = ['opening', 'count', 'damage', 'other'] as const;
export type AdjustReason = (typeof ADJUST_REASONS)[number];

export const BINS = ['stock', 'defective'] as const;
/** Sellable stock, or the defective bin that holds pieces waiting to go back to a vendor. */
export type Bin = (typeof BINS)[number];

// ---------------------------------------------------------------------------
// Master data: ordinary editable rows, not part of the ledger
// ---------------------------------------------------------------------------

export interface Item {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly origin: string;
  readonly unit: Unit;
  /** Low-stock alert threshold. */
  readonly minQty: Milli;
  readonly sellPrice: Paisa;
  readonly active: boolean;
}

export interface Party {
  readonly id: string;
  readonly name: string;
  readonly type: PartyType;
  readonly phone: string;
  readonly note: string;
  readonly active: boolean;
}

export interface Account {
  readonly id: string;
  readonly name: string;
  readonly kind: AccountKind;
  readonly active: boolean;
}

export interface Master {
  readonly items: ReadonlyMap<string, Item>;
  readonly parties: ReadonlyMap<string, Party>;
  readonly accounts: ReadonlyMap<string, Account>;
}

// ---------------------------------------------------------------------------
// Entries: the append-only khata. Never edited, only voided.
// ---------------------------------------------------------------------------

export interface EntryBase {
  readonly id: string;
  /** Strictly increasing append order. The ledger is folded in seq order. */
  readonly seq: number;
  readonly date: ISODate;
  /** ISO timestamp of when the entry was recorded. */
  readonly createdAt: string;
  readonly note: string;
  /** Id of the user who recorded it. Absent on entries from before users existed. */
  readonly by?: string;
}

export interface SaleLine {
  readonly itemId: string;
  readonly qty: Milli;
  readonly unitPrice: Paisa;
  readonly amount: Paisa;
}

export interface PurchaseLine {
  readonly itemId: string;
  readonly qty: Milli;
  readonly unitCost: Paisa;
  readonly amount: Paisa;
}

export interface SaleEntry extends EntryBase {
  readonly type: 'sale';
  readonly no: number;
  /** null = walk-in customer, must be fully paid. */
  readonly partyId: string | null;
  readonly lines: ReadonlyArray<SaleLine>;
  readonly total: Paisa;
  readonly paid: Paisa;
  readonly accountId: string;
}

export interface PurchaseEntry extends EntryBase {
  readonly type: 'purchase';
  readonly no: number;
  /** null = cash purchase with no vendor, must be fully paid. */
  readonly partyId: string | null;
  readonly lines: ReadonlyArray<PurchaseLine>;
  readonly total: Paisa;
  readonly paid: Paisa;
  readonly accountId: string;
}

export interface PaymentEntry extends EntryBase {
  readonly type: 'payment';
  readonly partyId: string;
  /** in = received from a customer, out = paid to a vendor. */
  readonly direction: 'in' | 'out';
  readonly amount: Paisa;
  readonly accountId: string;
}

export interface ReturnEntry extends EntryBase {
  readonly type: 'return';
  /** sale = a customer brought goods back, purchase = we sent goods back to a vendor. */
  readonly kind: 'sale' | 'purchase';
  /** null = walk-in / no vendor: the amount moves through `accountId` instead of a party balance. */
  readonly partyId: string | null;
  readonly itemId: string;
  readonly qty: Milli;
  readonly amount: Paisa;
  readonly reason: ReturnReason;
  /** For sale returns: where the goods go. For purchase returns: where they come from. */
  readonly bin: Bin;
  readonly accountId: string;
}

export interface ExpenseEntry extends EntryBase {
  readonly type: 'expense';
  readonly purpose: string;
  readonly amount: Paisa;
  readonly accountId: string;
}

export interface DrawingEntry extends EntryBase {
  readonly type: 'drawing';
  readonly who: string;
  readonly amount: Paisa;
  readonly accountId: string;
}

export interface TransferEntry extends EntryBase {
  readonly type: 'transfer';
  readonly fromAccountId: string;
  readonly toAccountId: string;
  readonly amount: Paisa;
}

export interface StockAdjustEntry extends EntryBase {
  readonly type: 'stock_adjust';
  readonly itemId: string;
  readonly bin: Bin;
  /** Signed: positive adds to the bin, negative removes. */
  readonly qty: Milli;
  /** Required when adding (qty > 0); null when removing, which goes out at average cost. */
  readonly unitCost: Paisa | null;
  readonly reason: AdjustReason;
}

export interface AccountOpeningEntry extends EntryBase {
  readonly type: 'account_opening';
  readonly accountId: string;
  readonly amount: Paisa;
}

export interface PartyOpeningEntry extends EntryBase {
  readonly type: 'party_opening';
  readonly partyId: string;
  /** Positive = the party owes us, negative = we owe the party (same sign rule for customers and vendors). */
  readonly amount: Paisa;
}

export interface VoidEntry extends EntryBase {
  readonly type: 'void';
  readonly targetId: string;
  readonly reason: string;
}

export type Entry =
  | SaleEntry
  | PurchaseEntry
  | PaymentEntry
  | ReturnEntry
  | ExpenseEntry
  | DrawingEntry
  | TransferEntry
  | StockAdjustEntry
  | AccountOpeningEntry
  | PartyOpeningEntry
  | VoidEntry;

export type EntryType = Entry['type'];

export const ENTRY_TYPES = [
  'sale',
  'purchase',
  'payment',
  'return',
  'expense',
  'drawing',
  'transfer',
  'stock_adjust',
  'account_opening',
  'party_opening',
  'void',
] as const satisfies ReadonlyArray<EntryType>;

export type EntryOf<T extends EntryType> = Extract<Entry, { type: T }>;
